import { Component, Input, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { StorageService } from '../../../Common/services/storage.service';
import { VerificationService } from '../../services/verification.service';
import { ToastrService } from 'ngx-toastr';
import { UserLogin } from '../../../Common/models/user.login';
import { ProjectValidationResponse } from '../../models/projectvalidationresponse';
import { ValidationCriteriaConstants } from '../../../Common/Constants/data';
import { firstValueFrom } from 'rxjs';
import { ProjectValidationCriteria, ProjectValidationCriteriaWithValidation } from '../../../project-intake/models/projectvalidationcriteria';

@Component({
  selector: 'app-project-validation-criteria-response',
  templateUrl: './project-validation-criteria-response.component.html',
  styleUrl: './project-validation-criteria-response.component.css'
})
export class ProjectValidationCriteriaResponseComponent {
  @Input() imageID?: number;
  @Input() projectID?: number;
  @Input() projectStatus?: string;
  @Input() isFullScreen?: boolean;
  @Output() saveCompleted: EventEmitter<any> = new EventEmitter();
  @Output() clearCompleted: EventEmitter<any> = new EventEmitter();

  uInfo: UserLogin | null | undefined;
  displayText: string = "";
  projectValidationCriterias: ProjectValidationCriteria[] = [];
  isSaveButtonEnabled: boolean = false;
  isProjectValidationResponseExists: boolean = false;

  projectValidationCriteriasAndValidationResponse: ProjectValidationCriteriaWithValidation[] = [];

  constructor(
    private storageService: StorageService,
    private verificaionService: VerificationService,
    private toastr: ToastrService
  ) {
  }

  async ngOnInit() {
    this.uInfo = this.storageService.getItem<UserLogin>('UserInfo');
    await this.getProjectValidationCriterias();
    if (this.imageID) {
      await this.loadValidationResponsesByImageIDAsync(this.imageID);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['imageID']?.currentValue) {
      this.loadValidationResponsesByImageIDAsync(changes['imageID'].currentValue);
    }
  }

  /**
   * Get the List of ProjectValidationCriteria and the corresponding ProjectValidationCriteriaResponse
   */
  getProjectValidationCriterias() {
    let projectId = Number(this.projectID);
    this.displayText = "Loading...";
    return this.verificaionService.getProjectValidationCriterias(projectId).toPromise().then(res => {
      if (res && res.length > 0) {
        this.projectValidationCriterias = res;
        this.displayText = "";
        if (this.projectValidationCriterias.length === 0) {
          this.displayText = "No criterias available.";
        }
      }
      else {
        this.displayText = "No criterias available.";
      }
    });
  }

  /**
   * Load ProjectValidationResponse based on the Image Id      
   * @param imageId An Image Id
   */
  private async loadValidationResponsesByImageIDAsync(imageId: number): Promise<void> {
    try {
      const responses = await firstValueFrom(this.verificaionService.getProjectValidationResponses(imageId));
      this.isProjectValidationResponseExists = !!responses?.length;

      this.projectValidationCriteriasAndValidationResponse = this.buildCriteriaWithValidationResponses(
        this.projectValidationCriterias,
        responses ?? []
      );
    } catch (error) {
      console.error('Error loading validation responses:', error);
      this.isProjectValidationResponseExists = false;
      this.projectValidationCriteriasAndValidationResponse = this.buildCriteriaWithValidationResponses(this.projectValidationCriterias, []);
    }
  }

  /**
   * Build Criteria with Validation Response         
   * @param criteriaList ProjectValidationCriteria + ProjectValidationCriteriaResponse 
   * @param validationResponses ProjectValidationResponse
   * @returns ProjectValidationCriteriaResponse
   */
  buildCriteriaWithValidationResponses(
    criteriaList: ProjectValidationCriteria[],
    validationResponses: ProjectValidationResponse[]
  ): ProjectValidationCriteriaWithValidation[] {
    return criteriaList.map(criteria => {
      const relatedValidationResponses = validationResponses.filter(v =>
        v.projectValidationCriteriaID === criteria.projectValidationCriteriaID
      );

      let verificationValue: string | null = null;
      let multiVerificationValue: string[] = [];

      if (criteria.responseType === 'MultiSelect') {
        multiVerificationValue = relatedValidationResponses
          .map(r => r.verificationValue)
          .filter((v): v is string => !!v); // exclude nulls/undefined
      } else {
        const firstResponse = relatedValidationResponses.find(r => r.verificationValue != null);
        verificationValue = firstResponse?.verificationValue ?? null;
      }

      return {
        ...criteria,
        projectValidationResponses: relatedValidationResponses,
        verificationValue,
        multiVerificationValue
      };
    });
  }

  /// check box value updating    
  onCheckboxChange(event: Event, item: ProjectValidationCriteriaWithValidation): void {
    const target = event.target as HTMLInputElement;
    item.verificationValue = target.checked ? 'Checked' : '';
  }

  /// For Multi-select control
  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  /// Save validation responses for the current image
  /// Assumption: All previous responses are deleted in the API before saving new ones,
  /// so all new responses will have projectValidationResponseID = 0.
  saveValidationResponses(): void {
    const validationResponses: ProjectValidationResponse[] = [];

    this.projectValidationCriteriasAndValidationResponse.forEach(criteria => {
      // Handle MultiSelect criteria
      if (criteria.responseType === 'MultiSelect') {
        (criteria.multiVerificationValue || []).forEach(selectedValue => {
          const matchingCriteriaResponse = criteria.lstProjectValidationCriteriaResponse.find(
            r => r.responseValue === selectedValue
          );

          validationResponses.push({
            projectValidationResponseID: 0, // New record since previous ones are deleted
            projectValidationCriteriaID: criteria.projectValidationCriteriaID!,
            projectID: this.projectID!,
            imageID: this.imageID ?? 0,
            verificationValue: selectedValue,
            verifiedBy: this.uInfo?.userID ?? 0,
            verifiedOn: new Date(),
            projectValidationCriteriaResponseID: matchingCriteriaResponse?.projectValidationCriteriaResponseID ?? 0
          });
        });
      }
      // Handle Dropdown / (Custom Type: FreeText, Checked, Yes/No) 
      else {
        const verificationValue = criteria.verificationValue?.trim();
        if (verificationValue) {
          const matchingCriteriaResponse = criteria.lstProjectValidationCriteriaResponse.find(
            r => r.responseValue === verificationValue
          );

          validationResponses.push({
            projectValidationResponseID: 0, // New record
            projectValidationCriteriaID: criteria.projectValidationCriteriaID!,
            projectID: this.projectID!,
            imageID: this.imageID ?? 0,
            verificationValue: verificationValue,
            verifiedBy: this.uInfo?.userID ?? 0,
            verifiedOn: new Date(),
            projectValidationCriteriaResponseID: matchingCriteriaResponse?.projectValidationCriteriaResponseID ?? 0
          });
        }
      }
    });

    // Validation: Ensure at least one response has been entered
    const hasAtLeastOneValue = validationResponses.some(
      item => item.verificationValue && item.verificationValue.trim() !== ''
    );

    if (!hasAtLeastOneValue) {
      this.toastr.error(ValidationCriteriaConstants.Response_EmptyError_Message);
      return;
    }

    this.isSaveButtonEnabled = true;

    this.verificaionService.saveProjectValidationResponses(validationResponses).subscribe(
      (savedResponses: ProjectValidationResponse[]) => {
        this.isSaveButtonEnabled = false;

        // Update local references with saved response IDs
        this.updateProjectValidationResponseIDs(savedResponses);

        // Notify parent that save is complete
        this.saveCompleted.emit();
      },
      error => {
        this.isSaveButtonEnabled = false;
        console.error('Error saving responses', error);
        this.toastr.error('Failed to save responses.');
      }
    );
  }

  /**
 * Updates the projectValidationResponseIDs in the local criteria list after saving.
 * 
 * This ensures that duplicate entries are not created if the same image remains on the screen
 * (e.g., when the user is on the last image and doesn't navigate away).
 * 
 * For other images, the UI usually reloads new data, but for consistency across scenarios,
 * we always update the response IDs here.
 * 
 * @param savedResponses - The list of saved ProjectValidationResponse objects with updated projectValidationResponseID values.
 */
  private updateProjectValidationResponseIDs(savedResponses: ProjectValidationResponse[]): void {
    savedResponses.forEach(saved => {
      const criteria = this.projectValidationCriteriasAndValidationResponse.find(
        c => c.projectValidationCriteriaID === saved.projectValidationCriteriaID
      );

      if (criteria) {
        // Check if it's MultiSelect
        if (criteria.responseType === 'MultiSelect') {
          const existing = criteria.projectValidationResponses.find(
            r => r.verificationValue === saved.verificationValue
          );

          if (existing) {
            existing.projectValidationResponseID = saved.projectValidationResponseID;
          } else {
            criteria.projectValidationResponses.push(saved);
          }

        } else {
          // For single select/FreeText/etc.
          if (criteria.projectValidationResponses.length === 0) {
            criteria.projectValidationResponses = [saved];
          } else {
            criteria.projectValidationResponses[0].projectValidationResponseID = saved.projectValidationResponseID;
          }
        }
      }
    });
  }

  ///Delete criteria responses  
  removeResponses() {
    if (this.imageID) {
      this.removeCriteriaResponses();
      this.clearCompleted.emit();
    }
  }

  removeCriteriaResponses(): void {
    this.verificaionService.deleteResponses(Number(this.imageID)).subscribe({
      next: () => {
        // Clear all responses in projectValidationCriteriasAndValidationResponse
        this.projectValidationCriteriasAndValidationResponse.forEach(criteria => {
          criteria.verificationValue = null;
          criteria.multiVerificationValue = [];
          criteria.projectValidationResponses = [];
        });

        this.isProjectValidationResponseExists = false;
        // Optional success toast
        // this.toastr.success(ProjectConstants.VALIDATION_REMOVE_RESPONSE_SUCCESS); 
      },
      error: (error) => {
        console.error('Error deleting responses', error);
        this.toastr.error('Failed to remove responses.');
      }
    });
  }

}